import { useState } from "react";
import { X, Loader2, Lock, ArrowRight, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

/**
 * Password change modal.
 *
 * Two modes:
 *   - mode="self": user changes their own password (requires currentPassword)
 *   - mode="admin": admin sets a new password for another client (no current required)
 *     → requires `clientId` and `clientLabel` props
 */
export default function PasswordModal({
  open,
  onClose,
  mode = "self",
  clientId,
  clientLabel,
}) {
  const { authHeader, API, setToken } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;
  const isAdmin = mode === "admin";

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("Confirmation does not match.");
      return;
    }
    setSubmitting(true);
    try {
      if (isAdmin) {
        await axios.post(
          `${API}/admin/clients/${clientId}/reset-password`,
          { new_password: next },
          { headers: authHeader(), withCredentials: true }
        );
        toast.success("Password reset — share it with the client.");
      } else {
        const { data } = await axios.post(
          `${API}/auth/password`,
          { current_password: current, new_password: next },
          { headers: authHeader(), withCredentials: true }
        );
        if (data?.token && setToken) setToken(data.token);
        toast.success("Password updated.");
      }
      reset();
      onClose?.();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || err.message
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-10 bg-black/45 backdrop-blur-sm"
      data-testid="password-modal"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="w-full max-w-md bg-white rounded-[2rem] shadow-2xl border border-stone-200 overflow-hidden">
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-teal-700">
              {isAdmin ? "Admin password reset" : "Account security"}
            </div>
            <h2 className="font-display text-2xl font-semibold mt-1">
              {isAdmin ? `Reset password for ${clientLabel || "client"}` : "Change password"}
            </h2>
          </div>
          <button
            type="button"
            onClick={() => {
              reset();
              onClose?.();
            }}
            data-testid="password-modal-close"
            className="h-9 w-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="px-6 pt-4 pb-6 space-y-4" data-testid="password-form">
          {isAdmin && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-2xl p-3 text-xs text-amber-900">
              <ShieldAlert className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div>
                This will overwrite their current password. They&apos;ll need the new
                one to sign in — make sure you share it securely.
              </div>
            </div>
          )}

          {!isAdmin && (
            <LabeledInput
              label="Current password"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              placeholder="Your current password"
              testId="password-current"
            />
          )}
          <LabeledInput
            label="New password"
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            minLength={6}
            placeholder="At least 6 characters"
            testId="password-new"
          />
          <LabeledInput
            label="Confirm new password"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={6}
            placeholder="Repeat new password"
            testId="password-confirm"
          />

          <button
            type="submit"
            disabled={submitting}
            data-testid="password-submit-btn"
            className="group w-full inline-flex items-center justify-center gap-3 bg-stone-900 text-white py-3 rounded-full text-sm font-semibold hover:bg-teal-700 transition-colors disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Working…
              </>
            ) : (
              <>
                {isAdmin ? "Reset password" : "Update password"}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

function LabeledInput({ label, testId, ...rest }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-stone-600 mb-1.5 block pl-1">
        {label}
      </span>
      <span className="relative block">
        <span className="absolute inset-y-0 left-3 flex items-center text-stone-400">
          <Lock className="h-4 w-4" />
        </span>
        <input
          {...rest}
          data-testid={testId}
          className="w-full bg-[#FAF7F2] border border-stone-200 rounded-full pl-10 pr-4 py-3 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-teal-700 focus:bg-white focus:ring-4 focus:ring-teal-700/10 transition-colors"
        />
      </span>
    </label>
  );
}
